import Modal from "@Components/Modal";
import TenanciesSection from "../TenanciesSection";
import styles from "./viewMoreDetailsModal.module.scss";
import RoomFeaturedImages from "./components/RoomFeaturedImages/RoomFeaturedImages";
import Description from "./components/Description";
import Amenities from "./components/Amenities";
import throttle from "@Utils/throttle";
import { UIEventHandler, useRef, useState, memo, useEffect } from "react";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import Portal from "@Components/Portal";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import Footer from "./components/Footer";
import ArrowUpIcon from "@Icons/ArrowUpIcon";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useAnalytics } from "helpers/analytics";
import { PAGE } from "constants/configConstants";
import useTranslationHook from "hooks/useTranslationHook";
import useWhitelabel from "hooks/useWhitelabel";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE } from "@Utils/webApputils/analyticEvents";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "@Store/index";
import config from "@Config/index";
import { renderChinaNamedDate } from "@Utils/cnDisplayDate";
import fetchRoomActiveChildren from "@Pages/PropertyPage/data/fetchRoomActiveChildren";
import { getRatingLabel } from "@Pages/PropertyPage/components/ReviewsSectionV2/buildReviewsData";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FloorPlanIcon from "@Icons/FloorPlanIcon";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import useRoomTypeContext from "@Pages/PropertyPage/components/RoomTypeSection/context/useRoomTypeContext";
import { isAvailable } from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import getRoomNavigationOrder from "@Pages/PropertyPage/components/RoomTypeSection/components/RoomDetailsSidePanel/utils/getRoomNavigationOrder";
import { getRoomDetailsVariant, ROOM_DETAILS_SECTION } from "helpers/roomDetailsTracking";

type ViewMoreDetailsModalProps = {
  heading: string;
  // tenancies: any;
  roomTypeData: any;
  isSoldOut?: boolean;
  onClose: any;
  assetsLength: any;
  images: any;
  features: any;
  featuredImage: any;
  description: any;
  virtualView: any;
  availableFrom?: any;
  isFlexible?: boolean;
  shouldModalScroll?: any;
  hideFooter?: boolean;
  scrollToAmenities?: boolean;
  /** The page's active room type tab, for event attribution. */
  activeTab?: string;
  /**
   * Send the room details experiment events (`room_details_viewed` / `_closed`, P1D-5512) from
   * here. Off for the legacy card, which still sends its own.
   */
  trackViewEvents?: boolean;
};

const ViewMoreDetailsModal = ({
  heading,
  onClose,
  // tenancies,
  roomTypeData,
  isSoldOut,
  assetsLength,
  images,
  features,
  featuredImage,
  description,
  virtualView,
  availableFrom = "",
  isFlexible = false,
  shouldModalScroll = false,
  hideFooter = false,
  scrollToAmenities = false,
  activeTab = "",
  trackViewEvents = false,
}: ViewMoreDetailsModalProps) => {
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const [showMoreTenancyButton, setShowMoreTenancyButton] = useState<boolean>(true);
  const [tenancies, setTenancies] = useState<any[]>([]);
  const { isWhitelabel, whitelabelColors } = useWhitelabel();
  const {
    lastScreen,
    propertyData,
    userCurrency,
    userLanguage,
    reviewsRating: rawReviewsRating,
  } = useSelector(({ propertyPageData, appData }: AppState) => ({
    lastScreen: appData.lastScreen,
    propertyData: propertyPageData,
    userCurrency: appData.userCurrency,
    userLanguage: appData.userLanguage,
    reviewsRating: propertyPageData?.data?.reviews_rating,
  }));

  const reviewsRating = rawReviewsRating ? parseFloat(String(rawReviewsRating)) : 0;

  const modalPanelRef = useRef<HTMLDivElement>(null);
  const amenitiesRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslationHook();
  const ratingLabel = t(getRatingLabel(reviewsRating));
  const isReviewsSectionV2Enabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.REVIEWS_SECTION_V2) || config.IS_CHINA_APP;
  const isFloorPlansSectionEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_FLOOR_PLANS_SECTION) || config.IS_CHINA_APP;
  const floorPlanImages =
    roomTypeData?.images?.filter((image: any) => image.type === "floor_plan") || [];
  const { trackTenancyView } = useAnalytics();
  const { roomTypes } = useRoomTypeContext();
  const openedAtRef = useRef(Date.now());

  const handleFloorPlanClick = () => {
    gtagEventEmitter({
      action: "room-type-floor-plan-clicked",
      category: "room-type",
      label: "floor_plan_modal",
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: floorPlanImages,
      category: "floor-plan",
      currentItemIndex: 0,
      showViewRoomButton: false,
      config_id: roomTypeData.id,
    });
  };

  useEffect(() => {
    trackTenancyView(String(roomTypeData?.id), heading);
  }, []);

  const scrollToBottom = () => {
    if (modalPanelRef && modalPanelRef.current) {
      modalPanelRef.current.scroll({
        top: modalPanelRef.current.offsetHeight,
        left: 0,
        behavior: "smooth",
      });
    }
    gtagEventEmitter({
      action: "choose-tenancy-pill",
      category: PAGE.INVENTORY,
      label: "view_more_details",
    });
  };

  const scrollToAmenitiesSection = () => {
    if (amenitiesRef && amenitiesRef.current && modalPanelRef && modalPanelRef.current) {
      const amenitiesOffset = amenitiesRef.current.offsetTop;
      modalPanelRef.current.scroll({
        top: amenitiesOffset - 56, // Adding 56px padding from top
        left: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    logAppEvent({
      eventName: APP_EVENTS.PAGE_VIEWED,
      payload: {
        screen: APP_PAGE.ROOM_TYPE_DETAILS,
        previous_screen: lastScreen,
        canonical_name: propertyData?.canonical_name,
        property_id: propertyData?.data?.id,
        property_name: propertyData?.data?.name,
        region_id: propertyData?.data?.region_id,
      },
    });
  }, []);

  useEffect(() => {
    if (shouldModalScroll) scrollToBottom();
  }, [shouldModalScroll]);

  useEffect(() => {
    if (scrollToAmenities) {
      // Small delay to ensure modal is fully rendered
      setTimeout(scrollToAmenitiesSection, 100);
    }
  }, [scrollToAmenities]);

  // Room details experiment events (P1D-5512). Sent after the tenancies are known so the counts are
  // real, and in the same shape as the side panel's, so the two arms are comparable.
  // Outside the test the modal sends exactly the events it always did (from the card, at click).
  const emitLegacyEvent = (action: string) =>
    gtagEventEmitter({ action, category: PAGE.INVENTORY, label: heading, active_tab: activeTab });

  const emitViewed = (list: any[]) => {
    if (!trackViewEvents || !getRoomDetailsVariant()) return;
    const order = getRoomNavigationOrder(roomTypes?.data || []);
    const position = order.findIndex((room: any) => room.id === roomTypeData?.id);
    gtagEventEmitter({
      action: "room_details_viewed",
      category: PAGE.INVENTORY,
      label: heading,
      active_tab: activeTab,
      ...(getRoomDetailsVariant() ? { room_details_variant: getRoomDetailsVariant() } : {}),
      property_id: propertyData?.data?.id,
      room_type_id: roomTypeData?.id,
      room_type_name: heading,
      open_source: "view_more_details",
      room_type_position: position + 1,
      room_type_count: order.length,
      tenancy_count: list.length,
      sold_out_tenancy_count: list.filter((tenancy: any) => !isAvailable(tenancy)).length,
    });
  };

  const handleClose = (event?: any) => {
    if (trackViewEvents && !getRoomDetailsVariant()) {
      emitLegacyEvent("room_details_closed");
    } else if (trackViewEvents) {
      // `Modal` passes `true` for Escape, the click event for × / the overlay, null for Back.
      const fromEscape = event === true;
      const fromCloseButton = String(event?.currentTarget?.className || "").includes(
        styles.closeBtn,
      );
      gtagEventEmitter({
        action: "room_details_closed",
        category: PAGE.INVENTORY,
        label: heading,
        active_tab: activeTab,
        ...(getRoomDetailsVariant() ? { room_details_variant: getRoomDetailsVariant() } : {}),
        property_id: propertyData?.data?.id,
        room_type_id: roomTypeData?.id,
        close_method: fromEscape
          ? "escape"
          : (event && !fromCloseButton && "outside_click") || "close_button",
        room_types_viewed: 1,
        seconds_open: Math.round((Date.now() - openedAtRef.current) / 1000),
      });
    }
    onClose(event);
  };

  const fetchAllTenancies = async () => {
    let fetched: any[] = [];
    try {
      fetched = await fetchRoomActiveChildren(roomTypeData.id, { userCurrency, userLanguage });
      setTenancies(fetched);
    } catch (error) {
      console.error("Error fetching tenancies:", error);
      setTenancies([]);
    }
    emitViewed(fetched);
  };

  useEffect(() => {
    if (trackViewEvents && !getRoomDetailsVariant()) emitLegacyEvent("room_details_viewed");
    fetchAllTenancies();
  }, []);

  const handleModalScroll: UIEventHandler = throttle(() => {
    if (modalPanelRef && modalPanelRef.current && showMoreTenancyButton) {
      const observer = new IntersectionObserver((entries) => {
        const element = entries[0];
        if (element.isIntersecting) {
          setShowMoreTenancyButton(false);
          observer.unobserve(element.target);
        }
      });
      observer.observe(modalPanelRef.current);
    }
  }, 300);

  return (
    <Portal rootId="forPortal">
      <Modal
        modalRef={modalPanelRef}
        hideModal={handleClose}
        size={isDesktop ? "medium" : "fullScreen"}
        animation={isDesktop ? "opacity" : "bottomToTop"}
        className={formatClassNames(styles.container, isDesktop && styles.desktop)}
        zIndex={1000}
        onScroll={handleModalScroll}
        align="center"
        type="ViewMoreDetailsModal"
        title={heading}
        closeBtnClassName={formatClassNames(styles.closeBtn, isDesktop && styles.desktop)}
        isHistoryPush={!isDesktop}
      >
        <div>
          {/* Sold out message */}
          {/* {isSoldOut && <SoldOutInfo />} */}
          {!isDesktop && (
            <RoomFeaturedImages
              images={images}
              assetsLength={assetsLength}
              isDesktop={isDesktop}
              virtualView={virtualView}
            />
          )}
          <div className={formatClassNames(styles.modalBody, isDesktop && styles.desktop)}>
            {/* Gallery */}
            {isDesktop && (
              <RoomFeaturedImages
                images={images}
                assetsLength={assetsLength}
                isDesktop={isDesktop}
                virtualView={virtualView}
              />
            )}
            {/* Rating + availability row */}
            {isReviewsSectionV2Enabled ? (
              <div className={styles.ratingRow}>
                {reviewsRating > 0 && (
                  <>
                    <div className={styles.ratingBadge}>
                      <span className={styles.ratingValue}>{reviewsRating.toFixed(1)}</span>
                      <span className={styles.ratingMax}>/5</span>
                    </div>
                    {ratingLabel && <span className={styles.ratingLabel}>{t(ratingLabel)}</span>}
                  </>
                )}
                {reviewsRating > 0 && !isDesktop && <div className={styles.mobileLineBreak} />}
                {!isSoldOut && (availableFrom || isFlexible) && (
                  <>
                    {reviewsRating > 0 && isDesktop && <span className={styles.ratingDot}>·</span>}
                    <span className={styles.availableTag}>
                      {isFlexible
                        ? t("propertypage.roomtypesectiondynamic.roomtypecard.flexiblemoveindates")
                        : `${t(
                            "propertypage.roomtypesectiondynamic.roomtypecard.availablefrom",
                          )}: ${
                            config.IS_CHINA_APP
                              ? renderChinaNamedDate(availableFrom, availableFrom)
                              : availableFrom
                          }`}
                    </span>
                  </>
                )}
                {isSoldOut && (
                  <>
                    {reviewsRating > 0 && isDesktop && <span className={styles.ratingDot}>·</span>}
                    <span className={styles.soldOutBadge}>
                      {t("propertypage.roomtypesectiondynamic.soldout") || "Sold Out"}
                    </span>
                  </>
                )}
                {isFloorPlansSectionEnabled && floorPlanImages.length > 0 && (
                  <>
                    {!isDesktop && <span className={styles.ratingDot}>·</span>}
                    <span
                      className={styles.viewFloorPlanLink}
                      onClick={handleFloorPlanClick}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handleFloorPlanClick();
                      }}
                      data-testid="modal-view-floor-plan"
                    >
                      <FloorPlanIcon size="16" />
                      <span>
                        {t("propertypage.roomtypesectiondynamic.roomtypecard.viewfloorplan")}
                      </span>
                    </span>
                  </>
                )}
              </div>
            ) : (
              <></>
            )}
            {/* Description Text */}
            <Description content={description} roomTypeId={roomTypeData?.id} />
          </div>
          {/* Amenities */}
          <div ref={amenitiesRef}>
            <Amenities features={features} isDesktop={isDesktop} />
          </div>

          {/* Tenancies */}
          <TenanciesSection
            tenancies={tenancies}
            roomTypeData={roomTypeData}
            isSoldOut={Boolean(isSoldOut)}
            featuredImage={featuredImage}
            isInsideModal
            isDesktop={isDesktop}
            activeTab={activeTab}
            sectionName={ROOM_DETAILS_SECTION.MODAL}
          />
          {!isDesktop && (
            <>
              <div className={styles.divider} />
              <div
                className={formatClassNames(
                  styles.modalBody,
                  isDesktop && styles.desktop,
                  styles.noBorder,
                )}
              >
                {!hideFooter && (
                  <Footer
                    type={roomTypeData?.meta?.unit_type}
                    id={roomTypeData?.id}
                    onClose={onClose}
                  />
                )}
              </div>
            </>
          )}
          {showMoreTenancyButton && (
            <div
              className={formatClassNames(styles.moreTenanciesPill, isDesktop && styles.desktop)}
            >
              <button type="button" onClick={scrollToBottom}>
                {t("propertypage.roomtypesectiondynamic.roomtypecard.chooseyourtenancy")}
                <ArrowUpIcon {...(isWhitelabel && { fill: whitelabelColors?.cta_color })} />
              </button>
            </div>
          )}
        </div>
      </Modal>
    </Portal>
  );
};

export default wrapperHOC(memo(ViewMoreDetailsModal), {
  componentName: "ViewMoreDetailsModal-RoomTypeCard-PropertyPage",
  showForChina: true,
});
